// src/pages/menu/sales/PipelinePage.js

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../../shared/api/apiClient";
import "./PipelinePage.css";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const normalize = (v) =>
  v === null || v === undefined ? "" : String(v).toLowerCase();

const SYSTEM_STAGE_NAMES = new Set(["won", "lost"]);

const isSystemStage = (stage) => {
  const name = String(stage?.name || "").trim().toLowerCase();
  return SYSTEM_STAGE_NAMES.has(name);
};

const PipelineRow = ({
  stage,
  onEdit,
  onDelete,
  disabled,
  dragDisabled,
  isSearching,
  systemLocked,
  t,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id, disabled: dragDisabled || systemLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : systemLocked ? 0.92 : 1,
  };

  const dragTitle = systemLocked
    ? t("pipeline_system_stage_locked")
    : dragDisabled
    ? isSearching
      ? t("pipeline_clear_search_to_reorder")
      : t("pipeline_reorder_disabled")
    : t("pipeline_drag_to_reorder");

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={isDragging ? "table-active" : ""}
    >
      <td style={{ width: 44 }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          title={dragTitle}
          {...attributes}
          {...listeners}
          disabled={disabled || dragDisabled || systemLocked}
          style={{
            cursor:
              disabled || dragDisabled || systemLocked ? "not-allowed" : "grab",
            opacity: systemLocked ? 0.55 : 1,
          }}
        >
          ☰
        </button>
      </td>

      <td>{stage.ui_id ?? "-"}</td>

      <td>
        <div className="d-flex align-items-center gap-2">
          <span>{stage.name}</span>
          {systemLocked ? (
            <span
              className="badge bg-secondary"
              title={t("pipeline_system_stage")}
            >
              {t("pipeline_system")}
            </span>
          ) : null}
        </div>
      </td>

      <td>{stage.position}</td>

      <td style={{ width: 180 }}>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => onEdit(stage)}
            disabled={disabled || systemLocked}
            title={systemLocked ? t("pipeline_system_stage_locked") : t("edit")}
          >
            {t("edit")}
          </button>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDelete(stage)}
            disabled={disabled || systemLocked}
            title={systemLocked ? t("pipeline_system_stage_locked") : t("delete")}
          >
            {t("delete")}
          </button>
        </div>
      </td>
    </tr>
  );
};

const PipelinePage = () => {
  const { t } = useTranslation();

  const calcShowingText = (total) =>
    total === 0
      ? t("showing_entries", { start: 0, end: 0, total: 0 })
      : t("showing_entries", { start: 1, end: total, total });

  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [formData, setFormData] = useState({ name: "", position: "" });

  const [searchQuery, setSearchQuery] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const fetchStages = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/pipeline-stages");
      const list = (res.data || [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setStages(list);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail || t("pipeline_error_load_stages")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSearching = searchQuery.trim().length > 0;

  const filteredStages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return stages;

    return stages.filter((s) => {
      const fields = [s.ui_id, s.name, s.position];
      return fields.some((f) => normalize(f).includes(q));
    });
  }, [stages, searchQuery]);

  const showingText = useMemo(
    () => calcShowingText(filteredStages.length),
    [filteredStages.length]
  );

  const openCreateModal = () => {
    setEditingStage(null);

    const nonSystemCount = stages.filter((s) => !isSystemStage(s)).length;
    const nextPos = nonSystemCount + 1;

    setFormData({ name: "", position: nextPos });
    setShowModal(true);
  };

  const openEditModal = (stage) => {
    if (isSystemStage(stage)) {
      alert(t("pipeline_alert_system_edit_forbidden"));
      return;
    }
    setEditingStage(stage);
    setFormData({ name: stage.name, position: stage.position });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const name = String(formData.name || "").trim();
      const position = Number(formData.position);

      if (!name || !position || position <= 0) {
        alert(t("pipeline_alert_fill_fields_correctly"));
        return;
      }

      const lower = name.toLowerCase();
      if (SYSTEM_STAGE_NAMES.has(lower)) {
        alert(t("pipeline_alert_reserved_stage_names"));
        return;
      }

      setError(null);

      if (editingStage) {
        if (isSystemStage(editingStage)) {
          alert(t("pipeline_alert_system_edit_forbidden"));
          return;
        }

        await api.put(`/pipeline-stages/${editingStage.id}`, { name, position });
      } else {
        await api.post("/pipeline-stages", { name, position });
      }

      setShowModal(false);
      await fetchStages();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || t("pipeline_error_save_failed"));
    }
  };

  const handleDelete = async (stage) => {
    if (isSystemStage(stage)) {
      alert(t("pipeline_alert_system_delete_forbidden"));
      return;
    }

    const confirmed = window.confirm(
      t("pipeline_confirm_delete_stage", { name: stage.name })
    );
    if (!confirmed) return;

    try {
      setError(null);
      await api.delete(`/pipeline-stages/${stage.id}`);
      await fetchStages();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || t("pipeline_error_delete_failed"));
    }
  };

  const persistPositions = async (orderedNonSystem) => {
    const systemStages = stages
      .filter((s) => isSystemStage(s))
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const nonSystemWithPositions = orderedNonSystem.map((s, idx) => ({
      ...s,
      position: idx + 1,
    }));

    const base = nonSystemWithPositions.length;
    const systemWithPositions = systemStages.map((s, idx) => ({
      ...s,
      position: base + idx + 1,
    }));

    const finalList = [...nonSystemWithPositions, ...systemWithPositions];

    const prevPosById = Object.fromEntries(stages.map((s) => [s.id, s.position]));
    const changed = nonSystemWithPositions.filter(
      (s) => (prevPosById[s.id] ?? null) !== s.position
    );

    setSavingOrder(true);
    setError(null);

    try {
      setStages(finalList);

      for (const s of changed) {
        await api.put(`/pipeline-stages/${s.id}`, {
          name: s.name,
          position: s.position,
        });
      }
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail || t("pipeline_error_save_order")
      );
      await fetchStages();
    } finally {
      setSavingOrder(false);
    }
  };

  const onDragEnd = async (event) => {
    if (isSearching) return;

    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const activeStage = stages.find((s) => s.id === active.id);
    const overStage = stages.find((s) => s.id === over.id);
    if (!activeStage || !overStage) return;

    if (isSystemStage(activeStage) || isSystemStage(overStage)) return;

    const nonSystem = stages.filter((s) => !isSystemStage(s));
    const oldIndex = nonSystem.findIndex((s) => s.id === active.id);
    const newIndex = nonSystem.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(nonSystem, oldIndex, newIndex);
    await persistPositions(newOrder);
  };

  const handleRefreshClick = async () => {
    setSearchQuery("");
    await fetchStages();
  };

  const disableActions = loading || savingOrder;
  const dragDisabled = disableActions || isSearching;

  return (
    <div className="container mt-4 pipelinePage">
      <div data-academy="pipeline-header" className="pipelinePageHeader d-flex justify-content-between align-items-center mb-3 gap-2 flex-nowrap">
        <h2 className="mb-0">{t("sales_pipeline")}</h2>

        <div className="d-flex gap-2 align-items-center flex-nowrap">
          <button
            data-academy="pipeline-create"
            className="btn btn-primary"
            onClick={openCreateModal}
            disabled={disableActions}
          >
            + {t("pipeline_add_stage")}
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshClick}
            disabled={disableActions}
          >
            {loading ? t("loading") + "..." : t("refresh")}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div data-academy="pipeline-toolbar" className="pipelineToolbar d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 px-2 pt-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="text-muted">{showingText}</div>

          {isSearching && (
            <span className="badge bg-info text-dark">
              {t("pipeline_reorder_disabled_while_searching")}
            </span>
          )}

          {savingOrder && (
            <span className="badge bg-warning text-dark">
              {t("saving")}...
            </span>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          <input
            className="form-control form-control-sm"
            style={{ width: 260 }}
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={disableActions}
          />
        </div>
      </div>

      <div className="pipelineMobileCard">
        <div className="pipelineMobileToolbar">
          <div className="pipelineMobileToolbarTitle">
            {t("table_filters", { defaultValue: "Table filters" })}
          </div>

          <div className="pipelineMobileMeta">
            <div>{showingText}</div>

            {isSearching && (
              <span className="pipelineMobileBadge pipelineMobileBadgeInfo">
                {t("pipeline_reorder_disabled_while_searching")}
              </span>
            )}

            {savingOrder && (
              <span className="pipelineMobileBadge pipelineMobileBadgeWarning">
                {t("saving")}...
              </span>
            )}
          </div>
        </div>

        <div className="pipelineMobileSearch">
          <input
            className="form-control"
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={disableActions}
          />
        </div>

        <div className="pipelineMobileList">
          {filteredStages.length === 0 && !loading && (
            <div className="pipelineMobileEmpty">
              {t("pipeline_empty")}
            </div>
          )}

          {filteredStages.map((stage) => {
            const systemLocked = isSystemStage(stage);

            return (
              <article
                className={`pipelineMobileItem ${
                  systemLocked ? "pipelineMobileItemLocked" : ""
                }`}
                key={stage.id}
              >
                <div className="pipelineMobileItemTop">
                  <div className="pipelineMobileItemTitleWrap">
                    <span className="pipelineMobileId">
                      #{stage.ui_id ?? "-"}
                    </span>

                    <h3>{stage.name}</h3>
                  </div>

                  <button
                    type="button"
                    className="pipelineMobileDrag"
                    title={
                      systemLocked
                        ? t("pipeline_system_stage_locked")
                        : dragDisabled
                        ? isSearching
                          ? t("pipeline_clear_search_to_reorder")
                          : t("pipeline_reorder_disabled")
                        : t("pipeline_drag_to_reorder")
                    }
                    disabled={disableActions || dragDisabled || systemLocked}
                  >
                    ☰
                  </button>
                </div>

                <div className="pipelineMobileInfoGrid">
                  <div>
                    <span>{t("position")}</span>
                    <strong>{stage.position}</strong>
                  </div>

                  <div>
                    <span>{t("status", { defaultValue: "Status" })}</span>
                    <strong>
                      {systemLocked
                        ? t("pipeline_system")
                        : t("custom", { defaultValue: "Custom" })}
                    </strong>
                  </div>
                </div>

                <div className="pipelineMobileActions">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => openEditModal(stage)}
                    disabled={disableActions || systemLocked}
                    title={
                      systemLocked
                        ? t("pipeline_system_stage_locked")
                        : t("edit")
                    }
                  >
                    {t("edit")}
                  </button>

                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleDelete(stage)}
                    disabled={disableActions || systemLocked}
                    title={
                      systemLocked
                        ? t("pipeline_system_stage_locked")
                        : t("delete")
                    }
                  >
                    {t("delete")}
                  </button>
                </div>
              </article>
            );
          })}

          {loading && (
            <div className="pipelineMobileEmpty">
              {t("loading")}...
            </div>
          )}
        </div>
      </div>

      <div data-academy="pipeline-table" className="table-responsive pipelineDesktopTable">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={(isSearching ? filteredStages : stages).map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <table className="table table-striped table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <th>{t("id")}</th>
                  <th>{t("subcategory_name") || t("name")}</th>
                  <th>{t("position")}</th>
                  <th style={{ width: 180 }}>{t("actions")}</th>
                </tr>
              </thead>

              <tbody>
                {filteredStages.length === 0 && !loading && (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      {t("pipeline_empty")}
                    </td>
                  </tr>
                )}

                {filteredStages.map((stage) => {
                  const systemLocked = isSystemStage(stage);

                  return (
                    <PipelineRow
                      key={stage.id}
                      stage={stage}
                      onEdit={openEditModal}
                      onDelete={handleDelete}
                      disabled={disableActions}
                      dragDisabled={dragDisabled}
                      isSearching={isSearching}
                      systemLocked={systemLocked}
                      t={t}
                    />
                  );
                })}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>

      {showModal && (
        <div className="modal show d-block" style={{ background: "#00000066" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingStage ? t("pipeline_edit_stage") : t("pipeline_create_stage")}
                </h5>

                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <label className="form-label">{t("subcategory_name") || t("name")}</label>
                <input
                  className="form-control mb-3"
                  placeholder={t("subcategory_name") || t("name")}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, name: e.target.value }))
                  }
                  disabled={disableActions}
                />

                <label className="form-label">{t("position")}</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder={t("position")}
                  value={formData.position}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      position: Number(e.target.value),
                    }))
                  }
                  disabled={disableActions}
                />

                <div className="form-text mt-2">
                  {t("pipeline_tip_position_reindexed")}
                  <br />
                  {t("pipeline_note_system_stages_fixed")}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={disableActions}
                >
                  {t("cancel")}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={disableActions}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelinePage;